import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import type { Identifier, XYCoord } from "dnd-core";

const PANEL_TYPE = "DASHBOARD_PANEL";
const SWAP_DELAY = 150;

interface DraggablePanelProps {
  id: string;
  index: number;
  children: React.ReactNode;
  movePanel: (dragIndex: number, hoverIndex: number) => void;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export default function DraggablePanel({ id, index, children, movePanel }: DraggablePanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lastSwapTime = useRef<number>(0);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: PANEL_TYPE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const now = Date.now();
      if (now - lastSwapTime.current < SWAP_DELAY) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      lastSwapTime.current = now;
      movePanel(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: PANEL_TYPE,
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const dragHandleRef = useRef<HTMLButtonElement>(null);

  preview(drop(ref));
  drag(dragHandleRef);

  return (
    <div ref={ref} data-handler-id={handlerId} className={`position-relative ${isDragging ? "opacity-50" : ""}`}>
      <button
        type="button"
        ref={dragHandleRef}
        title="Arrastar para reordenar"
        style={{ cursor: isDragging ? "grabbing" : "move" }}
        className="btn btn-link btn-icon-only position-absolute top-20 right-10 text-size-20 text-color-light user-select-none"
      >
        <span className="rioglyph rioglyph-drag-n-drop text-color-light" />
      </button>
      {children}
    </div>
  );
}
